/**
 * @fileoverview Redeeming a gift, /gift/redeem?code=GROW-XXXX-XXXX: where the gift email's
 * "Start your garden" button and the buyer's link go. Public.
 *
 * It reads the code first (who it is from, their message), then:
 *   - signed in: one button adds the year to the account (a year of Grow
 *     More, or a year added to their subscription) and says until when;
 *   - signed out: "I'm new: start my garden" (the sign-up page) or "I have an
 *     account: sign in". The code is kept in this browser and applied as soon
 *     as they are signed in (GiftClaim), so a new gardener signs up first and
 *     the gift follows.
 * A used, switched-off or unknown code says so plainly; without a code (or
 * after an unknown one) there is a box to type it in.
 * A failure on our side (a 500, no connection; D31, lib/serverFailure) that
 * is not one of the gift's own refusals (GIFT_UNAVAILABLE, a 503, stays
 * "Gifts aren't available yet"; GIFT_FAILED, a 502, is one of ours) says the standard sentence, reported to
 * Sentry, with the support address as a link, in place of
 * gift.redeem.failed's "Please try again in a moment" (lib/errorMessage).
 *
 * Where: the route /gift/redeem?code=&lang= (App.tsx), public, in GiftShell.
 * API: GET /gifts/code?code= (readGiftCode) for any code in the address;
 * POST /gifts/redeem { code } (redeemGift) when signed in, then the billing
 * status is read again.
 * Settings: ?lang=; the kept code in localStorage (GIFT_CODE_STORAGE_KEY,
 * lib/giftCode) for a visitor who signs in first.
 * Words (en/es): gift.redeem.*, sitewide.loading.
 * Watch out: "I'm new" goes to /today?start=1 (the sign-up screen) and "I have
 * an account" to /today; GiftClaim then redeems the kept code once the
 * account loads, so the gift follows the sign-up.
 *
 * @module GiftRedeemPage
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import Spinner from '@/shared/components/ui/Spinner';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { giftRefusalOf, readGiftCode, redeemGift, type GiftCodeState, type RedeemedGift } from '@/shared/api/gifts';
import { BILLING_QUERY_KEY } from '@/features/billing/lib/useBilling';
import { GiftShell } from './components/GiftShell';
import { useGiftLanguage } from './lib/useGiftLanguage';
import { forgetGiftCode, giftDay, keepGiftCode, tidyGiftCode } from './lib/giftCode';

/** What the page shows; a failure with what was thrown. */
type Shown =
  | { kind: 'ask' }
  | { kind: 'loading' }
  | { kind: 'code'; state: GiftCodeState }
  | { kind: 'redeeming'; state: GiftCodeState }
  | { kind: 'done'; result: RedeemedGift }
  | { kind: 'refused'; words: string; state: GiftCodeState | null }
  | { kind: 'failed'; error: unknown };

/** The page at /gift/redeem. */
export const GiftRedeemPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const language = useGiftLanguage();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mysqlUser } = useAuth();
  const code = tidyGiftCode(params.get('code') ?? '');
  const [shown, setShown] = React.useState<Shown>(code ? { kind: 'loading' } : { kind: 'ask' });
  const [typed, setTyped] = React.useState('');

  React.useEffect(() => {
    if (!code) { setShown({ kind: 'ask' }); return undefined; }
    let cancelled = false;
    setShown({ kind: 'loading' });
    readGiftCode(code)
      .then((state) => { if (!cancelled) setShown({ kind: 'code', state }); })
      .catch((error) => { if (!cancelled) setShown({ kind: 'failed', error }); });
    return () => { cancelled = true; };
  }, [code]);

  /**
   * Looks up the code typed in the box.
   *
   * @param event - The box's submit.
   */
  const look = (event: React.FormEvent): void => {
    event.preventDefault();
    const next = tidyGiftCode(typed);
    if (next) setParams({ code: next, lang: language });
  };

  /**
   * Adds the gift to the signed-in account.
   *
   * @param state - The code's state.
   */
  const redeem = async (state: GiftCodeState): Promise<void> => {
    if (!state.code) return;
    setShown({ kind: 'redeeming', state });
    try {
      const result = await redeemGift(state.code);
      forgetGiftCode();
      void queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
      setShown({ kind: 'done', result });
    } catch (error) {
      const refusal = giftRefusalOf(error);
      // No refusal, or GIFT_FAILED (a 502: Stripe would not add it): on our side.
      if (!refusal || refusal.code === 'GIFT_FAILED') { setShown({ kind: 'failed', error }); return; }
      const words = refusal.code === 'GIFT_USED' && refusal.mine ? t('gift.redeem.alreadyYours')
        : refusal.code === 'GIFT_USED' ? t('gift.redeem.used')
          : refusal.code === 'GIFT_VOID' ? t('gift.redeem.void')
            : refusal.code === 'GIFT_UNKNOWN' ? t('gift.redeem.unknown')
              : refusal.code === 'GIFT_TOO_FAR' ? t('gift.redeem.tooFar')
                : refusal.code === 'GIFT_UNAVAILABLE' ? t('gift.redeem.unavailable') : t('gift.redeem.failed');
      setShown({ kind: 'refused', words, state });
    }
  };

  /**
   * Keeps the code for after signing in, and goes to sign up or sign in.
   *
   * @param state - The code's state.
   * @param signUp - Open on signing up (a new gardener), or on signing in.
   */
  const signInFirst = (state: GiftCodeState, signUp: boolean): void => {
    if (state.code) keepGiftCode(state.code);
    navigate(signUp ? '/today?start=1' : '/today');
  };

  /** The code box, for no code or a code that is not one. */
  const codeBox = (
    <form className="mt-6 flex flex-wrap gap-2" onSubmit={look}>
      <label htmlFor="gift-code" className="sr-only">{t('gift.redeem.codeLabel')}</label>
      <Input
        id="gift-code"
        className="w-56 font-mono uppercase tracking-wider"
        placeholder="GROW-XXXX-XXXX"
        autoComplete="off"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
      <Button type="submit" variant="outline" disabled={!typed.trim()}>{t('gift.redeem.look')}</Button>
    </form>
  );

  /**
   * A ready code: who it is from, the message, and the way in.
   *
   * @param state - The code's state.
   * @param working - Whether it is being added now.
   */
  const ready = (state: GiftCodeState, working: boolean) => (
    <>
      <h1 className="font-grow text-3xl font-bold leading-tight">
        {state.from ? t('gift.redeem.fromTitle', { from: state.from }) : t('gift.redeem.title')}
      </h1>
      {state.message && (
        <blockquote className="mt-5 whitespace-pre-line border-l-4 border-primary bg-card px-4 py-3 italic">{state.message}</blockquote>
      )}
      <p className="mt-5 text-muted-foreground">{t('gift.redeem.what')}</p>
      <p className="mt-2 font-mono text-sm tracking-wider">{state.code}</p>
      {mysqlUser ? (
        <Button className="mt-6 w-full sm:w-auto" disabled={working} onClick={() => { void redeem(state); }}>
          {working ? <Spinner size={16} color="currentColor" /> : null}
          <span className={working ? 'ml-2' : ''}>{t('gift.redeem.add')}</span>
        </Button>
      ) : (
        <div className="mt-6 space-y-3">
          <Button className="w-full sm:w-auto" onClick={() => signInFirst(state, true)}>{t('gift.redeem.new')}</Button>
          <div>
            <Button variant="link" className="h-auto px-0" onClick={() => signInFirst(state, false)}>{t('gift.redeem.haveAccount')}</Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('gift.redeem.afterSignIn')}</p>
        </div>
      )}
    </>
  );

  return (
    <GiftShell>
      {shown.kind === 'ask' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.redeem.title')}</h1>
          <p className="mt-3 text-muted-foreground">{t('gift.redeem.typeIt')}</p>
          {codeBox}
        </>
      )}
      {shown.kind === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      )}
      {shown.kind === 'failed' && (
        <p role="alert" className="text-sm text-destructive">
          <FailureText text={errorMessage(shown.error, t('gift.redeem.failed'))} />
        </p>
      )}
      {shown.kind === 'code' && shown.state.status === 'ready' && ready(shown.state, false)}
      {shown.kind === 'redeeming' && ready(shown.state, true)}
      {shown.kind === 'code' && shown.state.status !== 'ready' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.redeem.title')}</h1>
          <p className="mt-3">
            {shown.state.status === 'used' ? t('gift.redeem.used')
              : shown.state.status === 'void' ? t('gift.redeem.void')
                : t('gift.redeem.unknown')}
          </p>
          {shown.state.status === 'unknown' && codeBox}
        </>
      )}
      {shown.kind === 'refused' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.redeem.title')}</h1>
          <p role="alert" className="mt-3"><FailureText text={shown.words} /></p>
          <p className="mt-6"><a href="/today" className="text-primary underline underline-offset-2">{t('gift.redeem.open')}</a></p>
        </>
      )}
      {shown.kind === 'done' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.redeem.doneTitle')}</h1>
          <p className="mt-3">
            {shown.result.how === 'subscription'
              ? t('gift.redeem.doneSubscription', { date: giftDay(shown.result.endsAt, language) })
              : t('gift.redeem.done', { date: giftDay(shown.result.endsAt, language) })}
          </p>
          <Button className="mt-6" onClick={() => navigate('/today')}>{t('gift.redeem.open')}</Button>
        </>
      )}
    </GiftShell>
  );
};

export default GiftRedeemPage;
